import React, { Component } from 'react';
import { trackThemeChange } from '../../../utils/analytics';
import './theme-toggle.css';

class ThemeToggle extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isDarkMode: false
        };
    }

    componentDidMount() {
        // Check if user has a saved theme preference
        const savedTheme = localStorage.getItem('theme');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        
        const shouldUseDark = savedTheme === 'dark' || (!savedTheme && prefersDark);
        
        if (shouldUseDark) {
            this.setState({ isDarkMode: true });
            document.documentElement.setAttribute('data-theme', 'dark');
        } else {
            this.setState({ isDarkMode: false });
            document.documentElement.removeAttribute('data-theme');
        }
        
        // Force chat theme application on initial load with retry logic
        this.forceInitialChatTheme(shouldUseDark);
        
        // Start periodic theme enforcement
        this.startPeriodicThemeCheck();
        
        // Also listen for window load event to catch late-loading chat widgets
        window.addEventListener('load', () => {
            setTimeout(() => {
                this.forceInitialChatTheme(shouldUseDark);
            }, 1000);
        });
        
        // Listen for DOMContentLoaded as backup
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                setTimeout(() => {
                    this.forceInitialChatTheme(shouldUseDark);
                }, 1500);
            });
        }
    }

    forceInitialChatTheme = (isDark) => {
        let attempts = 0;
        const maxAttempts = 10;
        const retryDelay = 500;

        const applyThemeWithRetry = () => {
            attempts++;
            
            // Try to apply theme
            this.updateChatTheme(isDark);
            
            // Check if chat elements exist and have been styled
            const chatElements = document.querySelectorAll('dta-chat, dta-chat-anchor');
            let hasStyledElements = Array.from(chatElements).some(element => {
                const hasCustomProperties = element.style.getPropertyValue('--dta-c-background') !== '';
                return hasCustomProperties;
            });

            // Also force styling on any message input fields found in shadow DOM
            chatElements.forEach(element => {
                if (element.shadowRoot) {
                    const messageInputs = element.shadowRoot.querySelectorAll('.message__input, [class*="message__input"], .message-input, [class*="message-input"]');
                    messageInputs.forEach(input => {
                        if (isDark) {
                            input.style.setProperty('background-color', '#3d3d3d', 'important');
                            input.style.setProperty('border', '1px solid #555555', 'important');
                            input.style.setProperty('color', '#ffffff', 'important');
                        } else {
                            input.style.removeProperty('background-color');
                            input.style.removeProperty('border');
                            input.style.removeProperty('color');
                        }
                        hasStyledElements = true; // Mark as styled if we found and processed inputs
                    });
                    
                    // Re-inject shadow styles to ensure everything is covered
                    this.injectShadowStyles(element.shadowRoot, isDark);
                }
            });

            // If no styled elements found and we haven't exceeded max attempts, retry
            if (!hasStyledElements && attempts < maxAttempts) {
                setTimeout(applyThemeWithRetry, retryDelay);
            } else if (hasStyledElements) {
                // Success - also force a re-render of shadow DOM styles
                setTimeout(() => {
                    chatElements.forEach(element => {
                        if (element.shadowRoot) {
                            this.injectShadowStyles(element.shadowRoot, isDark);
                        }
                    });
                }, 100);
            }
        };

        // Start the retry process
        setTimeout(applyThemeWithRetry, 200);
    };

    componentWillUnmount() {
        // Clean up chat observer
        if (this.chatObserver) {
            this.chatObserver.disconnect();
        }
        
        // Clean up periodic theme check
        if (this.themeCheckInterval) {
            clearInterval(this.themeCheckInterval);
        }
    }

    startPeriodicThemeCheck = () => {
        // Check and enforce theme every 3 seconds
        this.themeCheckInterval = setInterval(() => {
            const currentTheme = document.documentElement.getAttribute('data-theme');
            const isDarkMode = currentTheme === 'dark';
            
            // Force theme application to ensure consistency
            this.enforceThemeOnChatWidget(isDarkMode);
        }, 3000);
    };

    enforceThemeOnChatWidget = (isDark) => {
        const chatElements = document.querySelectorAll('dta-chat, dta-chat-anchor');
        
        chatElements.forEach(element => {
            // Check if the element has the correct theme applied
            const currentBg = element.style.getPropertyValue('--dta-c-background');
            
            // If theme doesn't match, force update
            if ((isDark && currentBg !== '#2d2d2d') || (!isDark && currentBg === '#2d2d2d')) {
                if (isDark) {
                    element.style.setProperty('--dta-c-background', '#2d2d2d');
                    element.style.setProperty('--dta-c-text', '#ffffff');
                    element.style.setProperty('--dta-c-primary', '#FF8900');
                    element.style.setProperty('--dta-c-shadow--rgba', 'rgba(255, 255, 255, 0.1)');
                    element.style.setProperty('--dta-chat-input-background-color', '#3d3d3d');
                    element.style.setProperty('--dta-chat-input-border-color', '#555555');
                    element.style.setProperty('--dta-chat-input-btn-bg-color', '#4d4d4d');
                    element.style.setProperty('--dta-chat-input-btn-bg-hv-color', '#5d5d5d');
                    element.style.setProperty('--dta-c-neutral-25', '#404040');
                    element.style.setProperty('--dta-c-text--dark', '#ffffff');
                    element.style.setProperty('--dta-color1', '#FF8900');
                    element.style.setProperty('--dta-color2', '#001C30');
                    element.style.colorScheme = 'dark';
                } else {
                    element.style.removeProperty('--dta-c-background');
                    element.style.removeProperty('--dta-c-text');
                    element.style.removeProperty('--dta-c-primary');
                    element.style.removeProperty('--dta-c-shadow--rgba');
                    element.style.removeProperty('--dta-chat-input-background-color');
                    element.style.removeProperty('--dta-chat-input-border-color');
                    element.style.removeProperty('--dta-chat-input-btn-bg-color');
                    element.style.removeProperty('--dta-chat-input-btn-bg-hv-color');
                    element.style.removeProperty('--dta-c-neutral-25');
                    element.style.removeProperty('--dta-c-text--dark');
                    element.style.removeProperty('--dta-color1');
                    element.style.removeProperty('--dta-color2');
                    element.style.colorScheme = 'light';
                }
                
                // Force shadow DOM styling as well
                if (element.shadowRoot) {
                    this.injectShadowStyles(element.shadowRoot, isDark);
                    
                    // Specifically target message inputs
                    const messageInputs = element.shadowRoot.querySelectorAll('.message__input, [class*="message__input"], .message-input, [class*="message-input"]');
                    messageInputs.forEach(input => {
                        if (isDark) {
                            input.style.setProperty('background-color', '#3d3d3d', 'important');
                            input.style.setProperty('border', '1px solid #555555', 'important');
                            input.style.setProperty('color', '#ffffff', 'important');
                        } else {
                            input.style.removeProperty('background-color');
                            input.style.removeProperty('border');
                            input.style.removeProperty('color');
                        }
                    });
                }
            }
        });
    };

    toggleTheme = () => {
        const newTheme = this.state.isDarkMode ? 'light' : 'dark';
        
        this.setState({ isDarkMode: !this.state.isDarkMode });
        
        // Update the document attribute
        if (newTheme === 'dark') {
            document.documentElement.setAttribute('data-theme', 'dark');
        } else {
            document.documentElement.removeAttribute('data-theme');
        }
        
        // Save preference to localStorage
        localStorage.setItem('theme', newTheme);
        
        // Track theme change with Google Analytics
        trackThemeChange(newTheme);
        
        // Update DTA chat theme
        this.updateChatTheme(newTheme === 'dark');
    };

    updateChatTheme = (isDark) => {
        // Wait a bit for DOM to be ready
        setTimeout(() => {
            const chatElements = document.querySelectorAll('dta-chat, dta-chat-anchor');
            
            chatElements.forEach(element => {
                if (isDark) {
                    // Apply dark theme styles
                    element.style.setProperty('--dta-c-background', '#2d2d2d');
                    element.style.setProperty('--dta-c-text', '#ffffff');
                    element.style.setProperty('--dta-c-primary', '#FF8900');
                    element.style.setProperty('--dta-c-shadow--rgba', 'rgba(255, 255, 255, 0.1)');
                    element.style.setProperty('--dta-chat-input-background-color', '#3d3d3d');
                    element.style.setProperty('--dta-chat-input-border-color', '#555555');
                    element.style.setProperty('--dta-chat-input-btn-bg-color', '#4d4d4d');
                    element.style.setProperty('--dta-chat-input-btn-bg-hv-color', '#5d5d5d');
                    element.style.setProperty('--dta-c-neutral-25', '#404040');
                    element.style.setProperty('--dta-c-text--dark', '#ffffff');
                    element.style.setProperty('--dta-color1', '#FF8900');
                    element.style.setProperty('--dta-color2', '#001C30');
                    element.style.colorScheme = 'dark';
                    
                    // Try to inject styles into shadow DOM if accessible
                    if (element.shadowRoot) {
                        this.injectShadowStyles(element.shadowRoot, true);
                    }
                } else {
                    // Reset to light theme
                    element.style.removeProperty('--dta-c-background');
                    element.style.removeProperty('--dta-c-text');
                    element.style.removeProperty('--dta-c-primary');
                    element.style.removeProperty('--dta-c-shadow--rgba');
                    element.style.removeProperty('--dta-chat-input-background-color');
                    element.style.removeProperty('--dta-chat-input-border-color');
                    element.style.removeProperty('--dta-chat-input-btn-bg-color');
                    element.style.removeProperty('--dta-chat-input-btn-bg-hv-color');
                    element.style.removeProperty('--dta-c-neutral-25');
                    element.style.removeProperty('--dta-c-text--dark');
                    element.style.removeProperty('--dta-color1');
                    element.style.removeProperty('--dta-color2');
                    element.style.colorScheme = 'light';
                    
                    // Remove injected styles from shadow DOM
                    if (element.shadowRoot) {
                        this.injectShadowStyles(element.shadowRoot, false);
                    }
                }
            });
            
            // Also try to update any chat content that might be dynamically loaded
            this.updateDynamicChatContent(isDark);
        }, 100);
    };

    injectShadowStyles = (shadowRoot, isDark) => {
        // Remove existing injected styles
        const existingStyle = shadowRoot.querySelector('#dta-theme-override');
        if (existingStyle) {
            existingStyle.remove();
        }
        
        if (isDark) {
            // Create and inject dark theme styles
            const style = document.createElement('style');
            style.id = 'dta-theme-override';
            style.textContent = `
                * {
                    color-scheme: dark !important;
                }
                
                /* General dark theme overrides */
                div, span, p, input, textarea {
                    background-color: #2d2d2d !important;
                    color: #ffffff !important;
                    border-color: #555555 !important;
                }
                
                /* Input fields */
                input, textarea {
                    background-color: #3d3d3d !important;
                    border: 1px solid #555555 !important;
                    color: #ffffff !important;
                }
                
                /* Specific targeting for message input field */
                .message__input, [class*="message__input"],
                .message-input, [class*="message-input"],
                input[class*="message"], textarea[class*="message"] {
                    background-color: #3d3d3d !important;
                    border: 1px solid #555555 !important;
                    color: #ffffff !important;
                }
                
                /* Control buttons - make transparent first */
                .dta-icon, [class*="dta-icon"],
                .maximize, [class*="maximize"],
                .close, [class*="close"],
                .btn-maximize, [class*="btn-maximize"],
                .btn-close, [class*="btn-close"],
                [class*="control"], [class*="header-btn"],
                button[class*="anchor"], .anchor-button, 
                [class*="chat-anchor"], [class*="floating"] {
                    background-color: transparent !important;
                    box-shadow: none !important;
                    backdrop-filter: none !important;
                    border: none !important;
                }
                
                
                /* Primary/accent colors */
                .primary, [class*="primary"] {
                    background-color: #FF8900 !important;
                    color: #ffffff !important;
                }
                
                /* Chat bubbles and containers */
                [class*="message"], [class*="bubble"], [class*="chat"] {
                    background-color: #2d2d2d !important;
                    color: #ffffff !important;
                }
                
                /* Remove box-shadow effects */
                * {
                    box-shadow: none !important;
                }
                
                /* Ensure anchor button has no background effects */
                button[class*="anchor"], .anchor-button, 
                [class*="chat-anchor"], [class*="floating"] {
                    background-color: transparent !important;
                    box-shadow: none !important;
                    backdrop-filter: none !important;
                }
            `;
            shadowRoot.appendChild(style);
        } else {
            // For light mode, inject styles to ensure clean reset
            const style = document.createElement('style');
            style.id = 'dta-theme-override';
            style.textContent = `
                /* Reset to ensure no gray backgrounds remain */
                .dta-icon, [class*="dta-icon"],
                .maximize, [class*="maximize"],
                .close, [class*="close"],
                .btn-maximize, [class*="btn-maximize"],
                .btn-close, [class*="btn-close"],
                [class*="control"], [class*="header-btn"],
                button[class*="anchor"], .anchor-button, 
                [class*="chat-anchor"], [class*="floating"] {
                    background-color: transparent !important;
                    box-shadow: none !important;
                    backdrop-filter: none !important;
                    border: none !important;
                }
            `;
            shadowRoot.appendChild(style);
        }
    };

    updateDynamicChatContent = (isDark) => {
        // Use MutationObserver to catch dynamically added chat content
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.addedNodes.length > 0) {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            // Check if it's a chat-related element
                            const chatElements = node.querySelectorAll ? 
                                node.querySelectorAll('dta-chat, dta-chat-anchor, [class*="dta"], [class*="chat"]') : [];
                            
                            chatElements.forEach(element => {
                                if (isDark) {
                                    element.style.setProperty('--dta-c-background', '#2d2d2d');
                                    element.style.setProperty('--dta-c-text', '#ffffff');
                                    element.style.setProperty('--dta-c-primary', '#FF8900');
                                } else {
                                    element.style.removeProperty('--dta-c-background');
                                    element.style.removeProperty('--dta-c-text');
                                    element.style.removeProperty('--dta-c-primary');
                                }
                                
                                // Force styling on message inputs specifically
                                if (element.shadowRoot) {
                                    const messageInputs = element.shadowRoot.querySelectorAll('.message__input, [class*="message__input"], .message-input, [class*="message-input"]');
                                    messageInputs.forEach(input => {
                                        if (isDark) {
                                            input.style.setProperty('background-color', '#3d3d3d', 'important');
                                            input.style.setProperty('border', '1px solid #555555', 'important');
                                            input.style.setProperty('color', '#ffffff', 'important');
                                        } else {
                                            input.style.removeProperty('background-color');
                                            input.style.removeProperty('border');
                                            input.style.removeProperty('color');
                                        }
                                    });
                                }
                            });
                        }
                    });
                }
            });
        });
        
        // Start observing
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        
        // Store observer to clean up later
        if (this.chatObserver) {
            this.chatObserver.disconnect();
        }
        this.chatObserver = observer;
    };

    render() {
        const { isDarkMode } = this.state;
        
        return (
            <button 
                className={`theme-toggle ${isDarkMode ? 'dark' : 'light'}`}
                onClick={this.toggleTheme}
                title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
                aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
            >
                <div className="toggle-icon">
                    {isDarkMode ? (
                        // Sun icon for light mode
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
                            <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                    ) : (
                        // Moon icon for dark mode
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" fill="currentColor"/>
                        </svg>
                    )}
                </div>
                <span className="toggle-text">
                    {isDarkMode ? 'Light' : 'Dark'}
                </span>
            </button>
        );
    }
}

export default ThemeToggle;